import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useLanguage } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { IndianRupee, CalendarDays, UserPlus, Star, Plus, Clock, ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const PIE_COLORS = ['#f0a500', '#1a1a2e', '#10b981', '#ef4444', '#6366f1', '#ec4899'];

const statusColors = {
  scheduled: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-amber-100 text-amber-800',
  Pending: 'bg-blue-100 text-blue-800',
  Confirmed: 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-blue-100 text-blue-800',
  Completed: 'bg-green-100 text-green-800',
  Cancelled: 'bg-red-100 text-red-800',
};

function formatLocalYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function statusTranslationKey(apt) {
  const map = {
    Pending: 'scheduled',
    Confirmed: 'scheduled',
    'In Progress': 'scheduled',
    scheduled: 'scheduled',
    completed: 'completed',
    cancelled: 'cancelled',
    Cancelled: 'cancelled',
    no_show: 'no_show',
  };
  const k = map[apt.status] || 'scheduled';
  return `booking_${k}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myBookingsToday, setMyBookingsToday] = useState([]);
  const { t } = useLanguage();

  const salonUser = JSON.parse(localStorage.getItem('salon_user') || '{}');
  const userRole = (salonUser.role || 'stylist').toLowerCase();
  const isOwnerOrManager = ['owner', 'manager'].includes(userRole);
  const isReceptionist = userRole === 'receptionist';
  const isStylist = userRole === 'stylist';

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch {
      toast.error(t('dash_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadStylistDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const u = JSON.parse(localStorage.getItem('salon_user') || '{}');
      const todayStr = formatLocalYMD(new Date());
      const [staffRes, bookRes] = await Promise.all([
        api.get('/staff'),
        api.get('/bookings', { params: { date: todayStr } }),
      ]);
      const staffList = Array.isArray(staffRes.data) ? staffRes.data : [];
      const me = staffList.find((s) => s.is_current_session_user);
      const myId = me?.id;
      const all = Array.isArray(bookRes.data) ? bookRes.data : [];
      let mine = [];
      if (myId) {
        mine = all.filter((b) => (b.staff_id || '') === myId);
      } else {
        const n = (u.name || '').trim();
        mine = n ? all.filter((b) => (b.staff_name || '').trim() === n) : [];
      }
      setMyBookingsToday(mine);
    } catch {
      toast.error(t('dash_load_error'));
      setMyBookingsToday([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const r = (JSON.parse(localStorage.getItem('salon_user') || '{}').role || 'stylist').toLowerCase();
    if (r === 'stylist') {
      loadStylistDashboard();
    } else {
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once; role from localStorage at mount
  }, []);

  const now = new Date();
  const timeStr = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  const dateStr = now.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const greeting =
    now.getHours() < 12 ? t('dash_morning') : now.getHours() < 17 ? t('dash_afternoon') : t('dash_evening');

  if (loading) {
    if (isStylist) {
      return (
        <div className="space-y-6">
          <Skeleton className="h-24 rounded-2xl w-full max-w-xl" />
          <Skeleton className="h-12 rounded-xl w-48" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-36 rounded-2xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-80 rounded-2xl lg:col-span-2" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (isStylist) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-poppins text-2xl lg:text-3xl font-bold text-[#1a1a2e]" data-testid="dashboard-heading">
              {greeting}, {salonUser.name || t('name')}
            </h1>
            <p className="text-gray-500 text-base mt-1">{dateStr}</p>
          </div>
          <div className="text-right sm:text-right">
            <p className="font-poppins text-3xl font-bold text-[#1a1a2e]">{timeStr}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3" data-testid="quick-actions">
          <Button
            onClick={() => navigate('/bookings')}
            data-testid="quick-new-booking"
            className="h-12 px-5 rounded-xl bg-[#1a1a2e] hover:bg-[#2d2d44] text-white gap-2 text-base"
          >
            <Plus className="w-5 h-5" /> {t('dash_new_booking')}
          </Button>
        </div>

        <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="font-poppins text-lg font-semibold">{t('my_schedule_today')}</CardTitle>
          </CardHeader>
          <CardContent>
            {myBookingsToday.length === 0 ? (
              <p className="text-center text-gray-400 py-10 text-base">{t('no_bookings_today_stylist')}</p>
            ) : (
              <div className="space-y-3">
                {myBookingsToday.map((apt) => (
                  <div key={apt.id} className="flex items-center justify-between bg-[#faf8f5] rounded-xl p-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="bg-[#1a1a2e] text-white w-12 h-12 rounded-xl flex flex-col items-center justify-center shrink-0">
                        <Clock className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1a1a2e] truncate">{apt.customer_name}</p>
                        <p className="text-sm text-gray-500 truncate">{apt.service_names?.join(', ')}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="font-medium text-[#1a1a2e]">{apt.time_slot}</p>
                      <Badge className={`${statusColors[apt.status] || 'bg-gray-100 text-gray-700'} text-xs mt-1`}>
                        {t(statusTranslationKey(apt))}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const allKpis = [
    { label: t('dash_today_rev'), value: `₹${(stats?.today_revenue || 0).toLocaleString('en-IN')}`, icon: IndianRupee, color: 'bg-[#f0a500]', textColor: 'text-[#1a1a2e]' },
    {
      label: t('dash_today_book'),
      value: stats?.today_bookings?.total || 0,
      sub: `${stats?.today_bookings?.scheduled ?? stats?.today_bookings?.pending ?? 0} ${t('dash_pending')}`,
      icon: CalendarDays,
      color: 'bg-[#1a1a2e]',
      textColor: 'text-white',
    },
    { label: t('dash_new_cust'), value: stats?.new_customers || 0, sub: t('dash_this_month'), icon: UserPlus, color: 'bg-emerald-500', textColor: 'text-white' },
    { label: t('dash_top_svc'), value: stats?.top_service || 'N/A', icon: Star, color: 'bg-purple-500', textColor: 'text-white' },
  ];

  const kpis = isReceptionist ? allKpis.filter((_, i) => i === 1 || i === 2) : allKpis;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-poppins text-2xl lg:text-3xl font-bold text-[#1a1a2e]" data-testid="dashboard-heading">
            {greeting}
          </h1>
          <p className="text-gray-500 text-base mt-1">{dateStr}</p>
        </div>
        <div className="text-right">
          <p className="font-poppins text-3xl font-bold text-[#1a1a2e]">{timeStr}</p>
        </div>
      </div>

      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-5 ${kpis.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-2'}`}
        data-testid="kpi-cards"
      >
        {kpis.map((kpi, i) => (
          <Card
            key={kpi.label}
            className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl overflow-hidden animate-fade-in-up"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`${kpi.color} ${kpi.textColor} p-3 rounded-xl`}>
                  <kpi.icon className="w-6 h-6" />
                </div>
              </div>
              <p className="text-sm text-gray-500 font-medium mb-1">{kpi.label}</p>
              <p className="font-poppins text-2xl font-bold text-[#1a1a2e] animate-count-up">{kpi.value}</p>
              {kpi.sub && <p className="text-sm text-gray-400 mt-1">{kpi.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-3" data-testid="quick-actions">
        <Button
          onClick={() => navigate('/bookings')}
          data-testid="quick-new-booking"
          className="h-12 px-5 rounded-xl bg-[#1a1a2e] hover:bg-[#2d2d44] text-white gap-2 text-base"
        >
          <Plus className="w-5 h-5" /> {t('dash_new_booking')}
        </Button>
        {(isOwnerOrManager || isReceptionist) && (
          <>
            <Button
              onClick={() => navigate('/billing')}
              data-testid="quick-new-bill"
              className="h-12 px-5 rounded-xl bg-[#f0a500] hover:bg-[#d69400] text-[#1a1a2e] gap-2 text-base font-semibold"
            >
              <Plus className="w-5 h-5" /> {t('dash_new_bill')}
            </Button>
            <Button
              onClick={() => navigate('/customers')}
              data-testid="quick-add-customer"
              variant="outline"
              className="h-12 px-5 rounded-xl gap-2 text-base border-2"
            >
              <UserPlus className="w-5 h-5" /> {t('dash_add_cust')}
            </Button>
          </>
        )}
      </div>

      {isOwnerOrManager && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="font-poppins text-lg font-semibold">{t('dash_rev_7')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats?.revenue_chart || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 13 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 13 }} stroke="#94a3b8" />
                    <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, t('dash_revenue')]} />
                    <Bar dataKey="revenue" fill="#f0a500" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="font-poppins text-lg font-semibold">{t('dash_svc_cat')}</CardTitle>
            </CardHeader>
            <CardContent>
              {(stats?.category_chart?.length || 0) > 0 ? (
                <div className="h-64 flex flex-col items-center justify-center">
                  <ResponsiveContainer width="100%" height="70%">
                    <PieChart>
                      <Pie data={stats.category_chart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                        {stats.category_chart.map((_, idx) => (
                          <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `₹${v.toLocaleString('en-IN')}`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 justify-center mt-2">
                    {stats.category_chart.map((c, idx) => (
                      <div key={c.name} className="flex items-center gap-1.5 text-sm">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} />
                        <span className="text-gray-600">{c.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400 text-base">{t('dash_no_billing')}</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border-0 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)] rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-poppins text-lg font-semibold">{t('dash_upcoming')}</CardTitle>
          <Button variant="ghost" onClick={() => navigate('/bookings')} className="text-[#f0a500] gap-1 font-medium" data-testid="view-all-bookings-btn">
            {t('dash_view_all')} <ArrowRight className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {(stats?.upcoming_appointments?.length || 0) > 0 ? (
            <div className="space-y-3">
              {stats.upcoming_appointments.map((apt) => (
                <div key={apt.id} className="flex items-center justify-between bg-[#faf8f5] rounded-xl p-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-[#1a1a2e] text-white w-12 h-12 rounded-xl flex flex-col items-center justify-center">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1a1a2e]">{apt.customer_name}</p>
                      <p className="text-sm text-gray-500">{apt.service_names?.join(', ')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-[#1a1a2e]">{apt.time_slot}</p>
                    <Badge className={`${statusColors[apt.status] || 'bg-gray-100 text-gray-700'} text-xs mt-1`}>
                      {t(statusTranslationKey(apt))}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8 text-base">{t('dash_no_upcoming')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
